// IntensityChart/index.tsx
import React, { useRef, useState, useMemo } from 'react';
import { MenuProps, Spin } from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Label,
  TooltipProps,
} from 'recharts';
import GraphDropDown from 'views/Dashboard/COBarChart/GraphDropdown';
import { downloadGraph } from 'shared/utils/downloadStaticGraph';
import { DownloadKeys, DownloadLoader } from 'enums/GraphDropdownEnums';
import CustomTooltip from 'shared/components/CustomisedTooltip';
import { CustomTick } from './customTick';
import { formatValue } from 'shared/utils/formatWholeNum';
import styles from './intensityChart.module.scss';
import {
  CompanyDashboardProps,
  customStyleAnchor,
  customPosition,
  customPositionX,
  customFontTick,
  customMarginLineChart,
} from '../constants';
import { CurveType } from 'recharts/types/shape/Curve';
import {
  formatAxisTickValue,
  getNiceYAxisScale,
} from 'shared/utils/chartScale';

interface EmissionIntensityLineChartProps {
  data: { year: number; intensity: number }[];
  AxisProp: string;
  unit: string;
  height?: number;
  hasDownload?: boolean;
  loading?: boolean;
  yAxisLabel?: string;
}

const EmissionIntensityLineChart: React.FC<EmissionIntensityLineChartProps> = ({
  data,
  AxisProp,
  unit,
  height = 350,
  hasDownload = true,
  loading,
  yAxisLabel,
}) => {
  const graphRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const yAxisScale = useMemo(
    () =>
      getNiceYAxisScale(
        data.map((d) => d.intensity),
        { tickCount: 4, anchorAtZero: true },
      ),
    [data]
  );

  const customPadding = useMemo(() => {
    return {
      left: data?.length === 1 ? 90 : 10,
      right: data?.length === 1 ? 400 : 0,
    };
  }, [data?.length]);

  const formatTick = useMemo(
    () => (value: number) => formatAxisTickValue(value, yAxisScale.ticks),
    [yAxisScale.ticks]
  );

  const renderTooltipContent = ({ payload }: TooltipProps<string, string>) => {
    if (!payload || !payload.length) return null;

    const point = payload[0].payload as { year: number; intensity: number };
    // Intensity values are already in correct units - use as-is
    const mapped = {
      ...point,
      totalCO2e: point.intensity,
    };

    return <CustomTooltip unit={unit} payload={mapped} />;
  };

  const handleDownload: MenuProps['onClick'] = async ({ key }) => {
    if (key === DownloadKeys.CSV) {
      const rows = [
        ['Year', yAxisLabel ?? `Emission Intensity (${unit})`],
        ...data.map((d) => [String(d.year), String(d.intensity ?? 0)]),
      ];
      const csv = rows.map((r) => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${CompanyDashboardProps.INTENSITY_CHART}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      return;
    }

    await downloadGraph(
      key,
      graphRef,
      CompanyDashboardProps.INTENSITY_CHART,
      setIsDownloading
    );
  };

  const isDataEmpty = !data || data.length === 0;

  return (
    <>
      {isDataEmpty ? (
        <p className={styles.emptyText}>
          {CompanyDashboardProps.DATA_EMPTY}
        </p>
      ) : (
        <Spin
          spinning={isDownloading || !!loading}
          {...(isDownloading && { tip: DownloadLoader.DOWNLOADING })}
        >
          <div className={styles.wrapper}>
            <div className={styles.positionedDiv}>
              <GraphDropDown
                handleDownload={handleDownload}
                showSort={false}
                isDownload={hasDownload}
                isMainDashboard
                handleSort={() => { }}
                graphRef={graphRef}
                setIsDownloading={setIsDownloading}
              />
            </div>

            <div ref={graphRef}>
              <ResponsiveContainer width="100%" height={height}>
                <LineChart data={data} margin={customMarginLineChart}>
                  <CartesianGrid
                    strokeDasharray="5 5"
                    horizontal={true}
                    vertical={false}
                  />

                  <XAxis
                    dataKey="year"
                    angle={-45}
                    dy={10}
                    dx={-10}
                    axisLine={false}
                    tickLine={false}
                    tick={<CustomTick x={20} y={0} payload={{ value: '' }} />}
                    padding={customPadding}
                  >
                    <Label
                      value={AxisProp}
                      position={customPositionX}
                      offset={-24}
                      style={customStyleAnchor}
                    />
                  </XAxis>

                  <YAxis
                    domain={yAxisScale.domain}
                    ticks={yAxisScale.ticks}
                    scale="linear"
                    tick={customFontTick}
                    axisLine={false}
                    tickFormatter={formatTick}
                    tickLine={false}
                  >
                    <Label
                      value={yAxisLabel ?? `Emission Intensity (${unit})`}
                      position={customPosition}
                      angle={-90}
                      offset={-10}
                      style={customStyleAnchor}
                    />
                  </YAxis>

                  <Line
                    type={CompanyDashboardProps.Linear as CurveType}
                    dataKey="intensity"
                    stroke={CompanyDashboardProps.LINE_STROKE}
                    strokeWidth={CompanyDashboardProps.LINE_STROKE_WIDTH}
                    dot={{ r: 2 }}
                    activeDot={{ r: 5 }}
                    label={({ x, y, value }) => (
                      <text
                        x={x}
                        y={y}
                        dy={-16}
                        dx={3}
                        fill={CompanyDashboardProps.LINE_FILL}
                        fontSize={12}
                        style={customStyleAnchor}
                      >
                        {formatValue(value)}
                      </text>
                    )}
                  />

                  <Tooltip cursor={false} content={renderTooltipContent} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Spin>
      )}
    </>
  );
};

export default EmissionIntensityLineChart;

